import { CSSProperties, Fragment, memo } from 'react'
import clsx from 'clsx'
import {
  Button,
  Card,
  Col,
  Form,
  FormListFieldData,
  Icon,
  Input,
  Label,
  Row,
} from 'components/common'

interface FooterProps {
  className?: string
  title: string
  onAdd?: () => void
  style?: CSSProperties
}

export interface ProductOptionsInterFace {
  options?: string
  productOptionsValues?: { optionsValue?: string | null }[]
}

export interface OptionsValue {
  jaJp?: string | null
  options?: string | null
  optionsValue?: string | null
}

interface OptionProps {
  productsOptionsName?: string[]
  productsOptionsValue?: OptionsValue[][] | null
  onLastOptionDelete: () => void
  haveDuplicateOptionValue?: boolean
  setHaveDuplicateOptionsValue?: (value: boolean) => void
}

export interface ProductOptionsProps {
  options?: OptionsValue[] | string[]
  optionsValue?: OptionsValue[] | string[]
  productOptionsValues?: OptionsValue[] | null
}

interface OptionValuesFormListProps {
  initialValue?: OptionsValue[] | null
  name: number
  onVariationDelete?: number
  productOptionsValues: FormListFieldData[]
  productOptionsValueRemove: (name: number) => void
  productOptionsInputFieldAdd: () => void
  haveDuplicateOptionValue?: boolean
  setHaveDuplicateOptionsValue?: (value: boolean) => void
}

const Footer = ({ title, className, onAdd, style }: FooterProps) => (
  <>
    <div className={className}>
      <Button
        style={style}
        type="link"
        onClick={() => {
          onAdd()
        }}
      >
        {title}
      </Button>
    </div>
  </>
)

const OptionValuesFormList = memo(
  ({
    initialValue,
    productOptionsValues,
    productOptionsValueRemove,
    productOptionsInputFieldAdd,
    haveDuplicateOptionValue,
    setHaveDuplicateOptionsValue,
  }: OptionValuesFormListProps): JSX.Element => (
    <>
      {productOptionsValues?.map(
        (
          { key: optionsValueKey, name: optionsValueName, ...restField },
          index
        ) => (
          <Fragment key={optionsValueKey}>
            <Row>
              <Col span={21}>
                <Form.Item
                  {...restField}
                  style={{ marginBottom: '5px' }}
                  name={[index, 'optionsValue']}
                  key={`${optionsValueKey}`}
                  fieldKey={[index, 'optionsValue']}
                  initialValue={
                    initialValue && initialValue?.[index] !== undefined
                      ? initialValue?.[index]?.jaJp
                      : ''
                  }
                  rules={[
                    ({ getFieldValue }) => ({
                      validator: async (_, currentValue) => {
                        const productsOptions = getFieldValue('productsOptions')
                        const productOptionsValues = []
                        productsOptions?.forEach((value) => {
                          value?.productOptionsValues?.forEach((options) =>
                            productOptionsValues.push(options?.optionsValue)
                          )
                        })
                        const matchValue = productOptionsValues?.filter(
                          (data) => data === currentValue
                        )
                        if (currentValue !== '' && matchValue?.length >= 2) {
                          setHaveDuplicateOptionsValue(false)
                          return Promise.reject(
                            new Error(
                              `すでにオプション値を使用しています "${currentValue}"`
                            )
                          )
                        } else {
                          setHaveDuplicateOptionsValue(true)
                        }
                      },
                    }),

                    { required: true, message: 'オプションの値は必須です。' },
                  ]}
                >
                  <Input autoComplete="off" />
                </Form.Item>
              </Col>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Col span={2}>
                  {productOptionsValues?.length > 1 ? (
                    <Button
                      danger
                      icon={<Icon.DeleteFilled />}
                      onClick={() => {
                        setHaveDuplicateOptionsValue(true)
                        productOptionsValueRemove(optionsValueName)
                      }}
                    />
                  ) : (
                    <Button />
                  )}
                </Col>
              </div>
            </Row>
          </Fragment>
        )
      )}
      {productOptionsValues?.length <= 99 && haveDuplicateOptionValue && (
        <Footer
          title={'オプションの値を追加する'}
          onAdd={productOptionsInputFieldAdd}
        />
      )}
    </>
  )
)

export const ProductOptions = memo(
  ({
    productsOptionsName,
    productsOptionsValue,
    onLastOptionDelete,
    haveDuplicateOptionValue,
    setHaveDuplicateOptionsValue,
  }: OptionProps): JSX.Element => {
    return (
      <>
        <Form.List name="productsOptions" initialValue={productsOptionsName}>
          {(productsOptions, { add, remove }) => (
            <>
              {productsOptions?.map(({ key, name, ...restField }, index) => (
                <Fragment key={key}>
                  <Row>
                    <Col span={21}>
                      <Form.Item
                        {...restField}
                        label="オプション名"
                        name={[index, 'options']}
                        fieldKey={[index, 'options']}
                        initialValue={
                          productsOptionsName ? productsOptionsName[index] : ''
                        }
                        rules={[
                          ({ getFieldValue }) => ({
                            validator: async (_, currentValue) => {
                              const productsOptions =
                                getFieldValue('productsOptions')
                              const productOptionsNames = []
                              productsOptions?.forEach((value) =>
                                productOptionsNames.push(value?.options)
                              )
                              const matchValue = productOptionsNames?.filter(
                                (data) => data === currentValue
                              )
                              if (
                                typeof currentValue !== 'undefined' &&
                                matchValue?.length >= 2
                              ) {
                                return Promise.reject(
                                  new Error(
                                    `オプションが重複しています "${currentValue}"`
                                  )
                                )
                              }
                            },
                          }),
                          {
                            required: true,
                            message: 'オプション名は必須です。',
                          },
                        ]}
                      >
                        <Input autoComplete="off" type="text" />
                      </Form.Item>
                    </Col>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: 30,
                      }}
                    >
                      <Col span={2}>
                        <Button
                          danger
                          icon={<Icon.DeleteFilled />}
                          onClick={() => {
                            onLastOptionDelete()
                            remove(name)
                          }}
                        />
                      </Col>
                    </div>
                  </Row>
                  <Label>オプションの値 </Label>
                  <Form.List
                    name={[index, 'productOptionsValues']}
                    initialValue={
                      productsOptionsValue && productsOptionsValue[index]
                        ? productsOptionsValue[index]
                        : [{ optionsValue: '' }]
                    }
                  >
                    {(
                      optionsValues,
                      {
                        add: productOptionsInputFieldAdd,
                        remove: productOptionsValueRemove,
                      }
                    ) => (
                      <OptionValuesFormList
                        name={name}
                        initialValue={
                          productsOptionsValue
                            ? productsOptionsValue?.[index]
                            : []
                        }
                        productOptionsValues={optionsValues}
                        productOptionsValueRemove={productOptionsValueRemove}
                        productOptionsInputFieldAdd={
                          productOptionsInputFieldAdd
                        }
                        haveDuplicateOptionValue={haveDuplicateOptionValue}
                        setHaveDuplicateOptionsValue={
                          setHaveDuplicateOptionsValue
                        }
                      />
                    )}
                  </Form.List>

                  {index < 2 && <Card.Divider />}
                </Fragment>
              ))}
              {productsOptions?.length <= 2 && haveDuplicateOptionValue && (
                <Footer
                  title={'オプションを追加する'}
                  onAdd={add}
                  className={clsx(
                    'flex',
                    'justify-end',
                    'items-center',
                    'm-10'
                  )}
                  style={{ position: 'absolute', right: '0px' }}
                />
              )}
            </>
          )}
        </Form.List>
      </>
    )
  }
)
